import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { SCHWELLE_MAX, VERSAND_FORMATE, type VersandFormat } from "@/api/versandregel";
import type { VersandStufenEntwurf } from "./versandstaffel";

const labelKlasse = "flex min-w-0 flex-col gap-1 text-xs font-semibold text-ink-2";
const selectKlasse = "h-9 min-w-0 rounded-md border border-line bg-panel px-2 text-sm font-normal text-ink disabled:opacity-60";

export function VersandformatFelder({ format, stufen, disabled, gueltig, onFormat, onStufen }: {
  format: VersandFormat | "";
  stufen: VersandStufenEntwurf[];
  disabled: boolean;
  gueltig: boolean;
  onFormat: (format: VersandFormat | "") => void;
  onStufen: (stufen: VersandStufenEntwurf[]) => void;
}) {
  const letzte = stufen.at(-1);
  const hinzufuegenMoeglich = format !== "" && format !== "paket" && letzte?.format !== "paket"
    && stufen.length < 20 && (!letzte || Number(letzte.abMenge) < SCHWELLE_MAX);
  function aendern(index: number, werte: Partial<VersandStufenEntwurf>) {
    onStufen(stufen.map((s, i) => i === index ? { ...s, ...werte } : s));
  }
  return (
    <div className="flex flex-col gap-3 border-b border-line-2 pb-4">
      <label className={labelKlasse}>
        Mindestformat
        <select aria-label="Mindestformat" value={format} disabled={disabled} className={selectKlasse}
          onChange={e => onFormat(e.target.value as VersandFormat | "")}>
          <option value="">Vom Ursprung übernehmen</option>
          {VERSAND_FORMATE.map(f => <option key={f.code} value={f.code}>{f.anzeige}</option>)}
        </select>
      </label>
      {stufen.map((s, index) => (
        <fieldset key={index} className="min-w-0 rounded-md border border-line px-3 pb-3">
          <legend className="px-1 text-xs text-ink-2">Mengenstufe {index + 1}</legend>
          <div className="grid items-end gap-2 sm:grid-cols-[8rem_minmax(0,1fr)_auto]">
            <label className={labelKlasse}>
              Ab Stückzahl
              <Input aria-label={`Mengenstufe ${index + 1} ab Stück`} type="number" inputMode="numeric"
                min={1} max={SCHWELLE_MAX} step={1} value={s.abMenge} disabled={disabled}
                aria-invalid={!gueltig} onChange={e => aendern(index, { abMenge: e.target.value })} />
            </label>
            <label className={labelKlasse}>
              Mindestens
              <select aria-label={`Mengenstufe ${index + 1} Mindestformat`} value={s.format} disabled={disabled}
                className={selectKlasse} onChange={e => aendern(index, { format: e.target.value as VersandStufenEntwurf["format"] })}>
                <option value="kleinpaket">Warenpost / Kleinpaket</option>
                <option value="paket">Paket</option>
              </select>
            </label>
            <Button type="button" size="sm" variant="outline" disabled={disabled}
              aria-label={`Mengenstufe ${index + 1} entfernen`} onClick={() => onStufen(stufen.filter((_, i) => i !== index))}>
              Entfernen
            </Button>
          </div>
        </fieldset>
      ))}
      <div>
        <Button type="button" size="sm" variant="outline" disabled={disabled || !hinzufuegenMoeglich}
          onClick={() => onStufen([...stufen, {
            abMenge: String(letzte ? Number(letzte.abMenge) + 1 : 2),
            format: letzte || format === "kleinpaket" ? "paket" : "kleinpaket",
          }])}>
          Mengenstufe hinzufügen
        </Button>
      </div>
      {!gueltig ? <p role="alert" className="text-xs text-red-700">Stückzahlen müssen zwischen 1 und 1000 liegen und aufsteigen. Jede folgende Stufe braucht ein größeres Format.</p> : null}
      <p className="text-xs text-ink-2">
        Beispiel: ab 2 Stück Warenpost / Kleinpaket, ab 3 Stück Paket. Ein 2er-Gebinde zählt als 2 physische Stück.
        Gewicht und Maß können ein größeres Format erfordern. Der Versanddienstleister wird im Versandablauf gewählt.
      </p>
    </div>
  );
}
